import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, ArrowRight, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { BRAND_PHOTOS, AW_LOGO } from '../lib/brand';
import { GrowthTracker } from '../components/landing/GrowthTracker';
import { InvestorsSection } from '../components/landing/InvestorsSection';
import { CommunitySection } from '../components/landing/CommunitySection';
import { TeamSection } from '../components/landing/TeamSection';

const NAV = [
  { href: '#growth', label: 'Growth' },
  { href: '#investors', label: 'Investors' },
  { href: '#community', label: 'Community' },
  { href: '#team', label: 'Team' },
  { href: '#locations', label: 'Locations' },
];

const LOCATIONS = [
  { name: 'A&W Elmwood (Moncton)', city: 'Moncton, NB', address: '26 Filles De Jesus Ave', phone: '(506) 384-2501', rating: 4.5 },
  { name: 'A&W Bathurst', city: 'Bathurst, NB', address: '1300 St Peter Avenue', phone: '(506) 546-4095', rating: 4.4 },
  { name: 'A&W Miramichi', city: 'Miramichi, NB', address: '102 Douglastown Blvd', phone: '(506) 778-2225', rating: 4.2 },
  { name: 'A&W White Rose Drive', city: "St. John's, NL", address: '40 White Rose Drive', phone: '(709) 753-7466', rating: 4.8 },
  { name: 'A&W Clarenville', city: 'Clarenville, NL', address: '6 Shoal Harbour Drive', phone: '(709) 466-7444', rating: 4.2 },
  { name: 'A&W Deer Lake', city: 'Deer Lake, NL', address: '8 Commerce Street', phone: '(709) 635-5800', rating: 4.7 },
  { name: 'A&W Rothesay', city: 'Rothesay, NB', address: '167 Hampton Road', phone: '(506) 849-8137', rating: 4.2 },
  { name: 'A&W Regent Mall', city: 'Fredericton, NB', address: '1381 Regent Street', phone: '(506) 459-7111', rating: 4.5 },
  { name: 'A&W McAllister Place', city: 'Saint John, NB', address: '131 McAllister Drive', phone: '(506) 657-1200', rating: 4.3 },
  { name: 'A&W Gander', city: 'Gander, NL', address: 'Gander, NL', phone: '', rating: 4.2 },
];

const GALLERY = [
  { src: BRAND_PHOTOS.angle, label: 'Our restaurants', caption: 'Modern A&W buildings built for Atlantic Canada' },
  { src: BRAND_PHOTOS.counter, label: 'Front counter', caption: 'Fresh, made-to-order and ready fast' },
  { src: BRAND_PHOTOS.dining, label: 'Dining room', caption: 'Bright, welcoming spaces for every guest' },
];

export default function LandingPage() {
  const [showLocations, setShowLocations] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      <header className="relative min-h-[88vh] flex flex-col overflow-hidden grain">
        <img src={BRAND_PHOTOS.front} alt="Elmwood A&W restaurant at golden hour" className="absolute inset-0 w-full h-full object-cover animate-ken-burns" data-testid="landing-hero-photo" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3E2418]/90 via-[#3E2418]/55 to-[#3E2418]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3E2418]/70 via-transparent to-black/10" />

        <nav className="relative z-10 px-6 sm:px-10 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={AW_LOGO} alt="A&W" className="w-12 h-12 object-contain drop-shadow-lg" />
              <div>
                <p className="text-lg font-black text-white leading-none tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood Management</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-orange-200 mt-1 font-semibold">A&amp;W Canada Franchisee</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-7 text-sm font-medium text-white/80" data-testid="landing-nav">
              {NAV.map(n => <a key={n.href} href={n.href} className="hover:text-white transition-colors">{n.label}</a>)}
            </div>
            <Link to="/login">
              <Button className="bg-white text-[#3E2418] hover:bg-orange-50 rounded-full px-6 font-semibold shadow-lg btn-press" data-testid="login-btn">
                Team Login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </nav>

        <div className="relative z-10 flex-1 flex items-center px-6 sm:px-10 pb-20">
          <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="text-[11px] uppercase tracking-[0.3em] text-orange-300 font-semibold mb-5">Est. 2023 · Moncton, New Brunswick</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.02] tracking-tight max-w-4xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Great burgers, great root beer,<br className="hidden sm:block" /> <span className="text-orange-400">great people.</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 mt-6 max-w-xl leading-relaxed">
                Elmwood Management operates 10 A&amp;W restaurants across New Brunswick and Newfoundland, with five more on the way — serving our communities with the A&amp;W experience they love.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-8">
                <button type="button" onClick={() => { setShowLocations(true); document.getElementById('locations')?.scrollIntoView({ behavior: 'smooth' }); }} className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#d9551a] text-white font-semibold rounded-full px-6 py-3 shadow-lg shadow-orange-500/30 transition-colors btn-press" data-testid="landing-view-locations-btn">
                  <MapPin className="w-4 h-4" /> Find a location
                </button>
                <Link to="/login" className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium px-2 py-3 transition-colors" data-testid="landing-hero-login-btn">
                  Team portal <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 reveal">
              <div className="glass-dark rounded-2xl p-6 text-white grid grid-cols-3 lg:grid-cols-1 gap-5 shadow-2xl" data-testid="landing-stats">
                <div>
                  <p className="text-4xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>15</p>
                  <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">A&amp;W locations · 10 open, 5 coming</p>
                </div>
                <div>
                  <p className="text-4xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>220+</p>
                  <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Team members</p>
                </div>
                <div>
                  <p className="text-4xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>$32M+</p>
                  <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Annual run rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="py-20 sm:py-28 px-6 sm:px-10" data-testid="landing-about">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#F26522] font-semibold mb-4">About Elmwood</p>
            <h2 className="text-3xl sm:text-4xl font-black text-[#3E2418] tracking-tight leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              From one restaurant on Elmwood Drive to ten across Atlantic Canada.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed text-base">
              A division of <strong className="text-[#3E2418]">Elmwood Capital Limited</strong>, Elmwood Management was founded in 2023 from our first location in Moncton. Today we operate 10 A&amp;W restaurants across New Brunswick and Newfoundland, with five more in development — a 15-location network and 220+ team members.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed text-base">
              Our teams are dedicated to the A&amp;W experience our communities love: great food, friendly service, and a commitment to excellence in every restaurant we run.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-6 gap-4" data-testid="landing-gallery">
            {GALLERY.map((g, i) => (
              <figure key={g.src} className={`group relative overflow-hidden rounded-2xl shadow-md ${i === 0 ? 'col-span-6 h-72 sm:h-80' : 'col-span-3 h-48 sm:h-56'}`}>
                <img src={g.src} alt={g.label} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3E2418]/85 via-[#3E2418]/10 to-transparent" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-orange-300 font-semibold">{g.label}</p>
                  <p className="text-sm sm:text-base font-semibold mt-1">{g.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <GrowthTracker />
      <InvestorsSection />
      <CommunitySection />
      <TeamSection />

      <footer id="locations" className="bg-[#3E2418] text-white scroll-mt-4">
        <div className="border-b border-white/10">
          <button onClick={() => setShowLocations(!showLocations)} className="w-full py-5 px-6 flex items-center justify-center gap-2 text-orange-200/80 hover:text-white transition-colors" data-testid="landing-locations-toggle">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">Our Locations ({LOCATIONS.length})</span>
            {showLocations ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showLocations && (
            <div className="px-6 pb-10" data-testid="landing-locations-list">
              <div className="max-w-6xl mx-auto">
                <p className="text-center text-white/50 text-sm mb-6">Open now across New Brunswick and Newfoundland — Tracadie, Hogan Court (NS), Salisbury, Richibucto and Shediac coming soon</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm stagger">
                  {LOCATIONS.map((loc) => (
                    <div key={loc.name} className="bg-white/5 hover:bg-white/10 rounded-xl p-4 border border-white/10 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-semibold text-white text-[13px]">{loc.name}</span>
                        <span className="flex items-center gap-1 text-amber-400 text-xs font-semibold"><Star className="w-3 h-3 fill-amber-400" />{loc.rating}</span>
                      </div>
                      <p className="text-white/60 text-xs">{loc.address}</p>
                      <p className="text-white/40 text-xs">{loc.city}</p>
                      {loc.phone && <a href={`tel:${loc.phone}`} className="text-orange-300 text-xs hover:text-orange-200 mt-1.5 inline-block">{loc.phone}</a>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="py-10 px-6">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={AW_LOGO} alt="A&W" className="w-10 h-10 object-contain" />
              <div>
                <h4 className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood Management</h4>
                <p className="text-xs text-white/50">A division of Elmwood Capital Limited</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/login" className="text-white/60 hover:text-orange-300 transition-colors">Team Login</Link>
              <a href="https://web.aw.ca" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-orange-300 transition-colors">A&amp;W Canada</a>
            </div>
          </div>
          <div className="text-center text-xs text-white/35 mt-8">
            © {new Date().getFullYear()} Elmwood Capital Limited. All rights reserved. &middot; Made by Mohit Chugh
          </div>
        </div>
      </footer>
    </div>
  );
}
